import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState, AppThunk } from 'store';
import createCountriesApi from 'api/countries';
import * as equivalentApi from 'api/equivalents';
import { showToast } from './toasterSlice';
import { FeatureFlags, FeatureGroup } from 'utils/featuresContext';
import { parseCountryToSelect, restrictedCountriesFilter } from 'utils/countries';
import { getPartnershipBranding } from 'api/branding';
import { getPartnerSettings, restoreStashedSettings, setPartnerSettings, stashSettings } from 'utils/partnerSettings';

interface State {
  context: PortalContext;
  countries: Country[];
  showOnboarding: boolean;
  isTourSkipped: boolean;
  equivalents: Equivalent[];
  theme?: string;
  features: { [key: string]: FeatureFlags };
  logo?: string | null;
  partnershipId?: string;
  languages?: string[];
  isExternalThemeLoaded?: boolean;
  featuresInOrder: FeaturesInOrder;
  feeDisplay: FeeDisplay;
  offsetTypesWithoutSaf: string[];
}

const initialState: State = {
  context: 'mychooose',
  countries: [],
  showOnboarding: false,
  equivalents: [],
  isTourSkipped: false,
  partnershipId: '',
  feeDisplay: {
    allowed: false,
    displayFeeOnPortals: false,
    displayFeeOnTermsAndConditions: false,
  },
  featuresInOrder: {
    wechooose: {},
    connect: {},
  },
  features: {
    'guest-wechooose': {},
    'guest-mychooose': {},
    mychooose: {},
    wechooose: {
      flights: {
        byDistance: false,
        fromTo: false,
        upload: false,
      },
      events: false,
      carbon: false,
      disablePercentageSlider: false,
      shipping: {
        voyage: false,
        preCalculatedVoyage: false,
      },
      groundFreight: false,
      railFreight: false,
      airFreight: false,
      teamPerk: false,
      cars: false,
      marketingToolkit: false,
      personalFlights: {
        byDistance: false,
        fromTo: false,
        byNumber: false,
      },
      personalEvents: false,
      personalCarbon: false,
      lifestyle: false,
      flightsFootprints: {
        byDistance: false,
        fromTo: false,
        upload: false,
      },
      airFreightFootprint: false,
      hideCertificateWatermarks: false,
    },
    connect: {
      flights: {
        byDistance: false,
        fromTo: false,
        upload: false,
      },
      events: false,
      carbon: false,
      shipping: {
        voyage: false,
        preCalculatedVoyage: false,
      },
      groundFreight: false,
      railFreight: false,
      airFreight: false,
      teamPerk: false,
      cars: false,
      marketingToolkit: false,
      personalFlights: {
        byDistance: false,
        fromTo: false,
        byNumber: false,
      },
      personalEvents: false,
      personalCarbon: false,
      lifestyle: false,
      hidePayAndShareButton: false,
      hideSendPaymentRequestButton: false,
      flightsFootprints: {
        byDistance: false,
        fromTo: false,
        upload: false,
      },
      airFreightFootprint: false,
    },
  },
  offsetTypesWithoutSaf: [],
};

const slice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setContext: (state: State, action: PayloadAction<PortalContext>): State => {
      state.context = action.payload;
      return state;
    },

    setCountries: (state: State, action: PayloadAction<Country[]>): State => {
      state.countries = action.payload.filter(country => !!country.codeIso2);
      return state;
    },

    setOnboarding: (state: State, action: PayloadAction<boolean>): State => {
      state.showOnboarding = action.payload;
      return state;
    },
    setEquivalents: (state: State, action: PayloadAction<Equivalent[]>): State => {
      state.equivalents = action.payload;
      return state;
    },
    setIsTourSkipped: (state: State, action: PayloadAction<boolean>): State => {
      state.isTourSkipped = action.payload;
      return state;
    },
    setFeatures: (state: State, action: PayloadAction<{ features: PortalFeatures[] | null; context: string }>): State => {
      const newFeatures: FeatureFlags = {};
      const oneTimeOffsetFeatures: Set<OffsetSubType> = new Set();
      const recurringOffsetFeatures: Set<OffsetSubType> = new Set();
      const flightOffsetFeatures: Set<FlightOffsetTypes> = new Set();
      const shippingOffsetFeatures: Set<ShippingOffsetTypes> = new Set();
      const oneTimeFootprintsFeatures: Set<OffsetSubType> = new Set();
      const flightFootprintsFeatures: Set<FlightOffsetTypes> = new Set();

      if (action.payload.features === null) {
        state.features[action.payload.context] = {};
        return state;
      }

      if (!Array.isArray(action.payload.features)) {
        return state;
      }

      action.payload.features.forEach(feature => {
        switch (feature) {
          case 'Flights_FromTo':
            newFeatures.flights = newFeatures.flights ?? {};
            (newFeatures.flights as FeatureGroup).fromTo = true;
            oneTimeOffsetFeatures.add('flights');
            flightOffsetFeatures.add('from/to');
            break;
          case 'Flights_ByDistance':
            newFeatures.flights = newFeatures.flights ?? {};
            (newFeatures.flights as FeatureGroup).byDistance = true;
            oneTimeOffsetFeatures.add('flights');
            flightOffsetFeatures.add('byDistance');
            break;
          case 'Flights_Upload':
            newFeatures.flights = newFeatures.flights ?? {};
            (newFeatures.flights as FeatureGroup).upload = true;
            oneTimeOffsetFeatures.add('flights');
            flightOffsetFeatures.add('upload');
            break;
          case 'FlightsFootprints_FromTo':
            newFeatures.flightsFootprints = newFeatures.flightsFootprints ?? {};
            (newFeatures.flightsFootprints as FeatureGroup).fromTo = true;
            oneTimeFootprintsFeatures.add('flights');
            flightFootprintsFeatures.add('from/to');
            break;
          case 'FlightsFootprints_ByDistance':
            newFeatures.flightsFootprints = newFeatures.flightsFootprints ?? {};
            (newFeatures.flightsFootprints as FeatureGroup).byDistance = true;
            oneTimeFootprintsFeatures.add('flights');
            flightFootprintsFeatures.add('byDistance');
            break;
          case 'FlightsFootprints_Upload':
            newFeatures.flightsFootprints = newFeatures.flightsFootprints ?? {};
            (newFeatures.flightsFootprints as FeatureGroup).upload = true;
            oneTimeFootprintsFeatures.add('flights');
            flightFootprintsFeatures.add('upload');
            break;
          case 'AirFreightFootprint':
            newFeatures.airFreightFootprint = true;
            oneTimeFootprintsFeatures.add('airFreight');
            break;
          case 'Events':
            newFeatures.events = true;
            oneTimeOffsetFeatures.add('events');
            break;
          case 'Carbon':
            newFeatures.carbon = true;
            oneTimeOffsetFeatures.add('carbon');
            break;
          case 'Shipping_Voyage':
            newFeatures.shipping = newFeatures.shipping ?? {};
            (newFeatures.shipping as FeatureGroup).voyage = true;
            oneTimeOffsetFeatures.add('shipping');
            shippingOffsetFeatures.add('voyageCalc');
            break;
          case 'Shipping_Precalculated':
            newFeatures.shipping = newFeatures.shipping ?? {};
            (newFeatures.shipping as FeatureGroup).preCalculatedVoyage = true;
            oneTimeOffsetFeatures.add('shipping');
            shippingOffsetFeatures.add('voyagePreCalc');
            break;
          case 'TeamPerk':
            newFeatures.teamPerk = true;
            recurringOffsetFeatures.add('teamperk');
            break;
          case 'Cars':
            newFeatures.cars = true;
            recurringOffsetFeatures.add('car');
            break;
          case 'MarketingToolkit':
            newFeatures.marketingToolkit = true;
            break;
          case 'PersonalFlights_FromTo':
            newFeatures.personalFlights = newFeatures.personalFlights ?? {};
            (newFeatures.personalFlights as FeatureGroup).fromTo = true;
            break;
          case 'PersonalFlights_ByDistance':
            newFeatures.personalFlights = newFeatures.personalFlights ?? {};
            (newFeatures.personalFlights as FeatureGroup).byDistance = true;
            break;
          case 'PersonalFlights_ByNumber':
            newFeatures.personalFlights = newFeatures.personalFlights ?? {};
            (newFeatures.personalFlights as FeatureGroup).byNumber = true;
            break;
          case 'PersonalEvents':
            newFeatures.personalEvents = true;
            break;
          case 'PersonalCarbon':
            newFeatures.personalCarbon = true;
            break;
          case 'Lifestyle':
            newFeatures.lifestyle = true;
            break;
          case 'RailFreight':
            newFeatures.railFreight = true;
            oneTimeOffsetFeatures.add('railFreight');
            break;
          case 'GroundFreight':
            newFeatures.groundFreight = true;
            oneTimeOffsetFeatures.add('groundFreight');
            break;
          case 'AirFreight':
            newFeatures.airFreight = true;
            oneTimeOffsetFeatures.add('airFreight');
            break;
          case 'DisablePercentageSlider':
            newFeatures.disablePercentageSlider = true;
            break;
          case 'HidePayAndShareButton':
            newFeatures.hidePayAndShareButton = true;
            break;
          case 'HideSendPaymentRequestButton':
            newFeatures.hideSendPaymentRequestButton = true;
            break;
          case 'HideCertificateWatermarks':
            newFeatures.hideCertificateWatermarks = true;
            break;
        }
      });

      state.features[action.payload.context] = { ...state.features[action.payload.context], ...newFeatures };
      if (action.payload.context === 'wechooose' || action.payload.context === 'connect') {
        state.featuresInOrder[action.payload.context].oneTimeOffsetFeatures = Array.from(oneTimeOffsetFeatures);
        state.featuresInOrder[action.payload.context].recurringOffsetFeatures = Array.from(recurringOffsetFeatures);
        state.featuresInOrder[action.payload.context].shippingOffsetFeatures = Array.from(shippingOffsetFeatures);
        state.featuresInOrder[action.payload.context].flightOffsetFeatures = Array.from(flightOffsetFeatures);
        state.featuresInOrder[action.payload.context].oneTimeFootprintsFeatures = Array.from(oneTimeFootprintsFeatures);
        state.featuresInOrder[action.payload.context].flightFootprintsFeatures = Array.from(flightFootprintsFeatures);
      }
      return state;
    },
    setOffsetTypesWithoutSaf: (state: State, action: PayloadAction<PortalFeatures[]>): State => {
      let newOffsetTypes: string[] = [];

      if (action.payload === null) {
        state.offsetTypesWithoutSaf = [];
        return state;
      }

      if (!Array.isArray(action.payload)) {
        return state;
      }

      action.payload.forEach(offsetType => {
        switch (offsetType) {
          case 'Flights_FromTo':
          case 'Flights_ByDistance':
          case 'Flights_Upload':
          case 'PersonalFlights_FromTo':
          case 'PersonalFlights_ByDistance':
          case 'PersonalFlights_ByNumber':
            newOffsetTypes = [...newOffsetTypes, 'flights'];
            break;
          case 'Events':
          case 'PersonalEvents':
            newOffsetTypes = [...newOffsetTypes, 'events'];
            break;
          case 'Carbon':
          case 'PersonalCarbon':
            newOffsetTypes = [...newOffsetTypes, 'carbon'];
            break;
          case 'Shipping_Voyage':
          case 'Shipping_Precalculated':
            newOffsetTypes = [...newOffsetTypes, 'shipping'];
            break;
          case 'TeamPerk':
            newOffsetTypes = [...newOffsetTypes, 'teamPerk'];
            break;
          case 'Cars':
            newOffsetTypes = [...newOffsetTypes, 'cars'];
            break;
          case 'Lifestyle':
            newOffsetTypes = [...newOffsetTypes, 'lifestyle'];
            break;
          case 'RailFreight':
            newOffsetTypes = [...newOffsetTypes, 'railFreight'];
            break;
          case 'GroundFreight':
            newOffsetTypes = [...newOffsetTypes, 'groundFreight'];
            break;
          case 'AirFreight':
            newOffsetTypes = [...newOffsetTypes, 'airFreight'];
            break;
        }
      });

      state.offsetTypesWithoutSaf = newOffsetTypes;

      return state;
    },
    setTheme: (state: State, action: PayloadAction<string>): State => {
      state.theme = action.payload;
      return state;
    },
    setPartnershipId: (state: State, action: PayloadAction<string>): State => {
      state.partnershipId = action.payload;
      return state;
    },
    setLogo: (state: State, action: PayloadAction<string | undefined | null>): State => {
      state.logo = action.payload;
      return state;
    },
    setLanguages: (state: State, action: PayloadAction<string[] | undefined>): State => {
      state.languages = action.payload;
      return state;
    },
    setExternalThemeLoaded: (state: State, action: PayloadAction<boolean>): State => {
      state.isExternalThemeLoaded = action.payload;
      return state;
    },
    setFeeDisplay: (state: State, action: PayloadAction<FeeDisplay | null>): State => {
      if (action.payload === null) {
        return state;
      }
      if (action.payload.allowed) {
        state.feeDisplay.allowed = action.payload.allowed;
      }
      if (action.payload.displayFeeOnPortals) {
        state.feeDisplay.displayFeeOnPortals = action.payload.displayFeeOnPortals;
      }
      if (action.payload.displayFeeOnTermsAndConditions) {
        state.feeDisplay.displayFeeOnTermsAndConditions = action.payload.displayFeeOnTermsAndConditions;
      }
      return state;
    },
  },
});

const {
  setContext,
  setCountries,
  setOnboarding,
  setEquivalents,
  setIsTourSkipped,
  setFeatures,
  setOffsetTypesWithoutSaf,
  setLogo,
  setPartnershipId,
  setTheme,
  setLanguages,
  setExternalThemeLoaded,
  setFeeDisplay,
} = slice.actions;
export {
  setContext,
  setCountries,
  setOnboarding,
  setIsTourSkipped,
  setFeatures,
  setLogo,
  setPartnershipId,
  setTheme,
  setLanguages,
  setFeeDisplay,
  setOffsetTypesWithoutSaf,
};

export default slice.reducer;

export const loadCountries = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const countryApi = createCountriesApi(context);
  try {
    const countries = await countryApi.getCountries();
    dispatch(setCountries(countries));
  } catch {
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
  }
};

export const loadEquivalents = (kilosCo2: number): AppThunk => async (dispatch): Promise<void> => {
  try {
    const equivalents = await equivalentApi.getEquivalents({ kilosCo2, language: 'en' });
    const properEquivalents = [
      'great_pyramid',
      'colosseum',
      'victoria_falls',
      'forest_grown_us',
      'forest_preserved_us',
      'laptop',
      'iphone12',
      'tree_seedlings',
      'easter_island_statues',
      'petrol',
      'avocado',
      'latte',
      'black_coffee',
      'household_electricity_nl',
    ];
    dispatch(setEquivalents(equivalents.filter(equivalent => properEquivalents.includes(equivalent.id))));
  } catch {
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
  }
};

export const loadAdditionalTheme = (partnershipId: string): AppThunk => async (dispatch): Promise<void> => {
  try {
    const data = await getPartnershipBranding(partnershipId);

    const settings = getPartnerSettings();
    stashSettings();
    setPartnerSettings({ ...settings, ...data });
    dispatch(setLogo(data.logoUrl));

    const element = document.getElementById('favicon') as HTMLLinkElement;
    if (!element || (!data?.faviconUrl && !data?.logoUrl)) {
      return;
    }
    element.href = data.faviconUrl || data.logoUrl;
    dispatch(setExternalThemeLoaded(true));
  } catch {
    // no settings found, doing nothing
    dispatch(setExternalThemeLoaded(false));
  }
};

export const unloadAdditionalTheme = (): AppThunk => async (dispatch): Promise<void> => {
  restoreStashedSettings();
  const settings = getPartnerSettings();

  const element = document.getElementById('favicon') as HTMLLinkElement;
  if (!element || (!settings?.faviconUrl && !settings?.logoUrl)) {
    return;
  }
  element.href = settings.faviconUrl || settings.logoUrl;
  dispatch(setLogo(settings.logoUrl));
  dispatch(setExternalThemeLoaded(false));
};

export const appContextSelector = createSelector(
  (state: RootState) => state.app,
  app => app.context,
);

export const logoSelector = createSelector(
  (state: RootState) => state.app,
  app => app.logo,
);
export const partnershipIdSelector = createSelector(
  (state: RootState) => state.app,
  app => app.partnershipId,
);

export const countriesSelector = createSelector(
  (state: RootState) => state.app,
  app => app.countries,
);

export const onboardingSelector = createSelector(
  (state: RootState) => state.app,
  app => app.showOnboarding,
);

export const featuresSelector = createSelector(
  (state: RootState) => state.app,
  app => app.features[app.context],
);

export const isRecurringOffsetEnabledSelector = createSelector(
  (state: RootState) => state.app,
  app => !!((app.features[app.context] as FeatureGroup)?.cars || (app.features[app.context] as FeatureGroup)?.teamPerk),
);

export const isShippingEnabledSelector = createSelector(
  (state: RootState) => state.app,
  app => {
    const shipping = (app.features[app.context] as FeatureGroup)?.shipping as FeatureGroup;

    return !!(shipping?.voyage || shipping?.preCalculatedVoyage);
  },
);

export const isFlightOffsetEnabledSelector = createSelector(
  (state: RootState) => state.app,
  app => {
    const flights = (app.features[app.context] as FeatureGroup)?.flights as FeatureGroup;

    return !!(flights?.byDistance || flights?.fromTo || flights?.upload);
  },
);

export const isEventOffsetEnabledSelector = createSelector(
  (state: RootState) => state.app,
  app => !!(app.features[app.context] as FeatureGroup)?.events,
);

export const isCarbonOffsetEnabledSelector = createSelector(
  (state: RootState) => state.app,
  app => !!(app.features[app.context] as FeatureGroup)?.carbon,
);

export const isAirFreightOffsetEnabledSelector = createSelector(
  (state: RootState) => state.app,
  app => !!(app.features[app.context] as FeatureGroup)?.airFreight,
);

export const isOneTimeOffsetEnabledSelector = createSelector(
  isFlightOffsetEnabledSelector,
  isEventOffsetEnabledSelector,
  isShippingEnabledSelector,
  isCarbonOffsetEnabledSelector,
  isAirFreightOffsetEnabledSelector,
  (flights, events, shipping, carbon, airFreight) => !!(flights || events || shipping || carbon || airFreight),
);

export const isOffsetEnabledSelector = createSelector(
  isRecurringOffsetEnabledSelector,
  isOneTimeOffsetEnabledSelector,
  (recurring, oneTime) => !!(recurring || oneTime),
);

export const isFacilitatedShippingEnabledSelector = createSelector(
  (state: RootState) => state.app,
  app => {
    const shipping = (app.features.connect as FeatureGroup)?.shipping as FeatureGroup;

    return !!(shipping?.voyage || shipping?.preCalculatedVoyage);
  },
);

export const isFacilitatedFlightOffsetEnabledSelector = createSelector(
  (state: RootState) => state.app,
  app => {
    const flights = (app.features.connect as FeatureGroup)?.flights as FeatureGroup;

    return !!(flights?.byDistance || flights?.fromTo || flights?.upload);
  },
);

export const isFacilitatedCarbonOffsetEnabledSelector = createSelector(
  (state: RootState) => state.app,
  app => !!(app.features.connect as FeatureGroup)?.carbon,
);

export const isFacilitatedAirFreightOffsetEnabledSelector = createSelector(
  (state: RootState) => state.app,
  app => !!(app.features.connect as FeatureGroup)?.airFreight,
);

export const isFacilitatedOneTimeOffsetEnabledSelector = createSelector(
  isFacilitatedFlightOffsetEnabledSelector,
  isFacilitatedShippingEnabledSelector,
  isFacilitatedCarbonOffsetEnabledSelector,
  isFacilitatedAirFreightOffsetEnabledSelector,
  (flights, shipping, carbon, airFreight) => !!(flights || shipping || carbon || airFreight),
);

export const isFacilitatedOffsetEnabledSelector = createSelector(isFacilitatedOneTimeOffsetEnabledSelector, oneTime => !!oneTime);

export const oneTimeOffsetsInOrderSelector = createSelector(
  (state: RootState) => state.app,
  app => app.featuresInOrder[app.context].oneTimeOffsetFeatures,
);

export const recurringOffsetsInOrderSelector = createSelector(
  (state: RootState) => state.app,
  app => app.featuresInOrder[app.context].recurringOffsetFeatures,
);

export const flightOffsetsInOrderSelector = createSelector(
  (state: RootState) => state.app,
  app => app.featuresInOrder[app.context].flightOffsetFeatures,
);

export const shippingOffsetsInOrderSelector = createSelector(
  (state: RootState) => state.app,
  app => app.featuresInOrder[app.context].shippingOffsetFeatures,
);

export const oneTimeFootprintsFeaturesInOrderSelector = createSelector(
  (state: RootState) => state.app,
  app => app.featuresInOrder[app.context].oneTimeFootprintsFeatures,
);

export const themeNameSelector = createSelector(
  (state: RootState) => state.app,
  app => app.theme ?? '',
);

export const normalizedCountriesSelector = createSelector(countriesSelector, countries =>
  countries.map(parseCountryToSelect).sort((countryA, countryB) => {
    if (countryA.label < countryB.label) {
      return -1;
    } else if (countryA.label > countryB.label) {
      return 1;
    }
    return 0;
  }),
);

export const normalizedCountriesExcludingRestrictedSelector = createSelector(normalizedCountriesSelector, countries =>
  countries.filter(restrictedCountriesFilter),
);

export const countriesWithAverageSelector = createSelector(normalizedCountriesSelector, countries => [
  { label: 'Average', value: 'EU' },
  ...countries,
]);

export const supportedCountriesISO2Selector = createSelector(countriesSelector, countries =>
  countries
    .filter(country => country.apiSupport.percapitaFootprint.inPopulationSource && country.apiSupport.percapitaFootprint.inPikSource)
    .map(parseCountryToSelect)
    .sort((countryA, countryB) => {
      if (countryA.label < countryB.label) {
        return -1;
      } else if (countryA.label > countryB.label) {
        return 1;
      }
      return 0;
    }),
);

export const isGuestContextSelector = createSelector(
  (state: RootState) => appContextSelector(state),
  context => ['guest-wechooose', 'guest-mychooose'].includes(context),
);

export const hasSkippedTour = createSelector(
  (state: RootState) => state.app,
  app => app.isTourSkipped,
);

export const isCorporateThemeSelector = createSelector(
  (state: RootState) => state.app,
  app => app.theme === 'corporate',
);

export const themeUnsetSelector = createSelector(
  (state: RootState) => state.app,
  app => !app.theme,
);

export const hidePayAndShareButtonSelector = createSelector(
  (state: RootState) => state.app,
  app => !!(app.features.connect as FeatureGroup)?.hidePayAndShareButton,
);

export const hideSendPaymentRequestButtonSelector = createSelector(
  (state: RootState) => state.app,
  app => !!(app.features.connect as FeatureGroup)?.hideSendPaymentRequestButton,
);

export const externalThemeLoadedSelector = createSelector(
  (state: RootState) => state.app,
  app => app.isExternalThemeLoaded,
);

export const flightFootprintsInOrderSelector = createSelector(
  (state: RootState) => state.app,
  app => app.featuresInOrder[app.context].flightFootprintsFeatures,
);

export const isFlightFootprintEnabledSelector = createSelector(
  (state: RootState) => state.app,
  app => {
    const flights = (app.features[app.context] as FeatureGroup)?.flightsFootprints as FeatureGroup;

    return !!(flights?.byDistance || flights?.fromTo || flights?.upload);
  },
);

export const isAirFreightFootprintEnabledSelector = createSelector(
  (state: RootState) => state.app,
  app => !!(app.features[app.context] as FeatureGroup)?.airFreightFootprint,
);

export const areFootprintsEnabledSelector = createSelector(
  isFlightFootprintEnabledSelector,
  isAirFreightFootprintEnabledSelector,
  (flights, airFreight) => !!flights || !!airFreight,
);

export const areFeesOnPortalHiddenSelector = createSelector(
  (state: RootState) => state.app,
  app => (app.theme === 'corporate' ? !app.feeDisplay.allowed || !app.feeDisplay.displayFeeOnPortals : false),
);
